import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { seminarService } from '../api/services';
import { Calendar, MapPin, Users, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

const SeminarRoomPage = () => {
  const { code }   = useParams();
  const [seminar,  setSeminar]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [name,     setName]     = useState('');
  const [joined,   setJoined]   = useState(false);

  useEffect(() => {
    seminarService.joinByCode(code)
      .then(res => {
        const s = res.data?.data?.seminar ?? res.data?.seminar;
        setSeminar(s);
      })
      .catch(err => setError(err.response?.data?.message || 'Invalid seminar code'))
      .finally(() => setLoading(false));
  }, [code]);

  const handleJoin = () => { if (name.trim() && seminar?.dailyRoomUrl) setJoined(true); };

  const date = seminar?.date
    ? new Date(seminar.date).toLocaleDateString('en-GH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const time = seminar?.date
    ? new Date(seminar.date).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })
    : '';

  if (loading) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center">
      <Loader2 size={36} className="animate-spin text-forest-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-forest-950 flex items-center justify-center px-4">
      <div className="text-center space-y-4 max-w-sm">
        <AlertCircle size={40} className="mx-auto text-rust-400" />
        <h3 className="font-serif text-xl text-cream-50">Seminar not found</h3>
        <p className="text-sm text-forest-400">{error}</p>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-forest-400 hover:text-cream-50 transition-colors">
          <ArrowLeft size={14} /> Back to TourEnt Hub
        </Link>
      </div>
    </div>
  );

  // ── Joined — show Daily.co room ──────────────────────────────
  if (joined) {
    const roomUrl = `${seminar.dailyRoomUrl}?userName=${encodeURIComponent(name)}`;
    return (
      <div className="min-h-screen bg-forest-950 flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 bg-forest-900 border-b border-forest-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-baseline gap-0.5">
              <span className="font-serif font-semibold text-sm text-white">TourEnt</span>
              <span className="font-serif font-semibold text-sm text-green-400"> Hub</span>
            </Link>
            <div className="h-4 w-px bg-forest-700" />
            <span className="text-sm text-forest-300 truncate max-w-xs">{seminar.title}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-xs text-green-400">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Live
            </span>
            <span className="text-xs text-forest-500">Joined as <strong className="text-forest-300">{name}</strong></span>
          </div>
        </div>
        <div className="flex-1">
          <iframe
            src={roomUrl}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="w-full h-full border-0"
            style={{ minHeight: 'calc(100vh - 53px)' }}
            title={seminar.title}
          />
        </div>
      </div>
    );
  }

  // ── Pre-join page ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-forest-950 relative overflow-hidden">

      {/* Full background image */}
      {seminar.imageUrl && (
        <div className="absolute inset-0">
          <img src={seminar.imageUrl} alt={seminar.title}
            className="w-full h-full object-cover" />
          <div className="absolute inset-0"
            style={{ background: 'linear-gradient(160deg, rgba(2,10,5,0.88) 0%, rgba(3,14,7,0.80) 50%, rgba(1,8,3,0.92) 100%)' }} />
        </div>
      )}

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 py-4 border-b border-white/10">
        <Link to="/" className="flex items-baseline gap-0.5">
          <span className="font-serif font-semibold text-lg text-white">TourEnt</span>
          <span className="font-serif font-semibold text-lg text-green-400"> Hub</span>
        </Link>
        <span className="text-xs text-white/40 flex items-center gap-1.5 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          {seminar.status === 'LIVE' ? 'Live Now' : 'Upcoming Session'}
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-10 space-y-8">

        {/* Host section */}
        <div className="text-center space-y-4">
          {/* Host image — large and prominent */}
          {seminar.imageUrl ? (
            <div className="relative inline-block">
              <img src={seminar.imageUrl} alt={seminar.hostName ?? seminar.title}
                className="w-28 h-28 rounded-full object-cover border-4 border-white/20 shadow-2xl mx-auto" />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full border-2 border-forest-950 flex items-center justify-center">
                <Calendar size={14} className="text-white" />
              </div>
            </div>
          ) : (
            <div className="w-28 h-28 rounded-full bg-forest-800 border-4 border-white/20 flex items-center justify-center mx-auto shadow-2xl">
              <Calendar size={36} className="text-forest-500" />
            </div>
          )}

          {seminar.hostName && (
            <div>
              <h2 className="font-serif text-2xl font-bold text-white">{seminar.hostName}</h2>
              {seminar.hostTitle && (
                <p className="text-green-400 text-sm font-medium mt-1">{seminar.hostTitle}</p>
              )}
            </div>
          )}

          <h1 className="font-serif text-3xl font-semibold text-white leading-tight">
            {seminar.title}
          </h1>
        </div>

        {/* Event details card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3 backdrop-blur-sm">
          <div className="flex items-center gap-3 text-white/80 text-sm">
            <Calendar size={15} className="text-green-400 flex-shrink-0" />
            <span>{date} at {time}</span>
          </div>
          {seminar.location && (
            <div className="flex items-center gap-3 text-white/80 text-sm">
              <MapPin size={15} className="text-green-400 flex-shrink-0" />
              <span>{seminar.location}</span>
            </div>
          )}
          {seminar.maxAttendees && (
            <div className="flex items-center gap-3 text-white/80 text-sm">
              <Users size={15} className="text-green-400 flex-shrink-0" />
              <span>Max {seminar.maxAttendees} attendees</span>
            </div>
          )}
          {seminar.description && (
            <p className="text-white/60 text-sm leading-relaxed pt-2 border-t border-white/10">
              {seminar.description}
            </p>
          )}
        </div>

        {/* Promo video */}
        {seminar.promoVideoUrl && (
          <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
            {seminar.promoVideoUrl.includes('youtube.com/embed') ? (
              <iframe
                src={seminar.promoVideoUrl}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full border-0"
                style={{ height: '240px' }}
                title="Promo video"
              />
            ) : (
              <video controls className="w-full bg-black" style={{ maxHeight: '240px' }}
                src={seminar.promoVideoUrl} />
            )}
          </div>
        )}

        {/* Join form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm space-y-4">
          <h3 className="font-serif font-semibold text-white text-lg text-center">Join This Session</h3>

          {seminar.dailyRoomUrl ? (
            <>
              <div>
                <label className="block text-xs font-medium text-white/50 mb-2 uppercase tracking-widest">Your name</label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleJoin()}
                  placeholder="Enter your name to join..."
                  className="w-full px-4 py-3.5 rounded-xl border border-white/10 bg-white/10 text-blue-500 placeholder-white/30 text-sm focus:outline-none focus:border-green-500/50 backdrop-blur-sm"
                />
              </div>
              <button
                onClick={handleJoin}
                disabled={!name.trim()}
                className="w-full py-4 rounded-xl bg-green-600 hover:bg-green-500 text-white font-semibold text-base transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                Join Seminar →
              </button>
            </>
          ) : (
            <div className="text-center space-y-3">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-4">
                <p className="text-sm text-amber-300 mb-1 font-medium">Session room not ready yet</p>
                <p className="text-xs text-amber-400/70">Check back closer to the start time.</p>
              </div>
              <button onClick={() => window.location.reload()}
                className="text-sm text-white/40 hover:text-white/70 transition-colors">
                ↻ Refresh to check again
              </button>
            </div>
          )}

          <p className="text-center text-xs text-white/30">
            No account needed · Anyone with the code can join
          </p>
        </div>

      </div>
    </div>
  );
};

export default SeminarRoomPage;